/**
 * Reading the reader's filters out of the URL, for the "Current view" download.
 *
 * The filters live in the URL — that is the panel's own convention, and it is
 * what makes a filtered view a thing people send each other. Reading them from
 * the same place the table reads them is what keeps the downloaded document and
 * the screen in agreement; deriving them from component state instead would give
 * two sources that can disagree after a Back navigation.
 *
 * Pure and separately tested. Getting this wrong has a specific, nasty failure
 * mode: a "Full report" quietly shaped by a filter, which is a file that claims
 * to be complete and is not. The two callers pass different things ON PURPOSE
 * and the tests assert the REQUEST rather than the row count, because a fixture
 * whose filter happens to match everything would pass either way.
 */

import type { AppliedFilterSummary } from "./ReportDocument";

/** The filter half of a candidates query — never the page or the sort. */
export interface CandidateFilterParams {
  name?: string;
  title?: string;
  company?: string;
  stage?: string[];
  highestStage?: string[];
  hasLinkedin?: boolean;
  hasEmail?: boolean;
  hasAssessment?: boolean;
  hasReferences?: boolean;
}

function presence(raw: string | null): boolean | undefined {
  if (raw === "has") return true;
  if (raw === "none") return false;
  return undefined;
}

/**
 * The filters currently in force, in the shape the client sends.
 *
 * Deliberately omits `page`, `sort` and `order`: a reader's PAGE is not part of
 * what they mean by "what I'm looking at" in a document that carries every row,
 * and the sort is applied by the export's own ordering.
 */
export function currentCandidateFilters(params: URLSearchParams): CandidateFilterParams {
  const filters: CandidateFilterParams = {};
  const name = params.get("name")?.trim();
  const title = params.get("title")?.trim();
  const company = params.get("company")?.trim();
  if (name) filters.name = name;
  if (title) filters.title = title;
  if (company) filters.company = company;
  const stage = params.getAll("stage");
  const highestStage = params.getAll("highest_stage");
  if (stage.length > 0) filters.stage = stage;
  if (highestStage.length > 0) filters.highestStage = highestStage;
  const linkedin = presence(params.get("linkedin"));
  const email = presence(params.get("email"));
  if (linkedin !== undefined) filters.hasLinkedin = linkedin;
  if (email !== undefined) filters.hasEmail = email;
  const assessment = presence(params.get("assessment"));
  const references = presence(params.get("references"));
  if (assessment !== undefined) filters.hasAssessment = assessment;
  if (references !== undefined) filters.hasReferences = references;
  return filters;
}

/**
 * The same filters as prose, for the document's provenance block.
 *
 * The two report filters match either report of their group (epic
 * 2026-10-22-candidate-reports-four-kinds, released 2026-10-26), as the chip
 * tooltips say, so the document names both.
 */
export function describeFilters(filters: CandidateFilterParams): AppliedFilterSummary[] {
  const described: AppliedFilterSummary[] = [];
  if (filters.name) described.push({ label: "Name", value: filters.name });
  if (filters.title) described.push({ label: "Title", value: filters.title });
  if (filters.company) described.push({ label: "Company", value: filters.company });
  if (filters.stage?.length) described.push({ label: "Stage", value: filters.stage.join(", ") });
  if (filters.highestStage?.length) {
    described.push({ label: "Highest stage", value: filters.highestStage.join(", ") });
  }
  if (filters.hasLinkedin !== undefined) {
    described.push({ label: "LinkedIn", value: filters.hasLinkedin ? "Has one" : "Missing" });
  }
  if (filters.hasEmail !== undefined) {
    described.push({ label: "Email", value: filters.hasEmail ? "Has one" : "Missing" });
  }
  if (filters.hasAssessment !== undefined) {
    described.push({
      label: "Assessment",
      value: filters.hasAssessment ? "Has one (recruiter or client)" : "Missing (neither)",
    });
  }
  if (filters.hasReferences !== undefined) {
    described.push({
      label: "References",
      value: filters.hasReferences ? "Has some (official or backchannel)" : "Missing (neither)",
    });
  }
  return described;
}

/**
 * The download's filename.
 *
 * The two modes MUST differ. Two files in a Downloads folder that are named the
 * same and contain different row counts is a trap: the browser silently appends
 * `(1)` and the reader has no way to tell which is the complete one without
 * opening both.
 */
export function documentFilename(
  searchName: string | undefined,
  mode: "full" | "view",
  section: "dashboard" | "progress" = "dashboard"
): string {
  const slug =
    (searchName ?? "search")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "search";
  const stamp = new Date().toISOString().slice(0, 10);
  const what = section === "progress" ? "progress" : "report";
  return `${slug}-${what}${mode === "view" ? "-filtered" : ""}-${stamp}.html`;
}
